/**
 * Persistent Worker lifecycle and correlated scheduling requests. One client belongs to one
 * verified browser session; the caller resets it on logout or token changes. Aborting a compute
 * terminates the Worker, which also interrupts a long synchronous WASM call.
 * @module apps/web/src/lib/schedulingWorkerClient
 */
import type {
  SchedulingBackend,
  SchedulingWorkerReply,
  SchedulingWorkerRequest,
  SchedulingWorkerResponse,
} from './schedulingProtocol';

/** One pending request and its deterministic cleanup operations. */
interface PendingRequest {
  /** Resolve a successful correlated reply. */
  resolve: (reply: SchedulingWorkerResponse) => void;
  /** Reject failures, cancellation or session reset. */
  reject: (error: Error) => void;
  /** Clear the deadline and optional abort listener. */
  cleanup: () => void;
  /** Parent-thread time when the request was posted. */
  started: number;
}

/** Browser-native Worker factory injected by tests to exercise lifecycle and race handling. */
export type SchedulingWorkerFactory = () => Worker;

/** A session-owned Worker; neither input snapshots nor results enter persistent browser storage. */
export class SchedulingWorkerClient {
  /** Lazily created worker, retained after successful warmup/solve. */
  private worker?: Worker;
  /** Correlation map, bounded to warmup plus one solve. */
  private pending = new Map<string, PendingRequest>();
  /** Create the isolated module Worker; tests can supply an inert transport. */
  constructor(
    /** Session-local factory; no Worker or private input is shared between owners. */
    private readonly factory: SchedulingWorkerFactory
  ) {}

  /** Initialize once before a click; errors remain optional-preview failures. */
  warm(backend: SchedulingBackend = 'auto'): Promise<SchedulingWorkerResponse> {
    return this.request('warm', undefined, undefined, backend);
  }

  /** Stage canonical changes under a bounded deadline without blocking React rendering. */
  solve(
    input: string,
    signal?: AbortSignal,
    backend: SchedulingBackend = 'auto'
  ): Promise<SchedulingWorkerResponse> {
    return this.request('solve', input, signal, backend);
  }

  /** Run the exact ghost algorithm on a dedicated client so its cancellation cannot interrupt a full solve. */
  ghosts(
    input: string,
    weekStart: string,
    signal?: AbortSignal
  ): Promise<SchedulingWorkerResponse> {
    return this.request('ghosts', input, signal, 'auto', weekStart);
  }

  /** Drop all private WASM memory and reject pending work on session switch or cancellation. */
  reset(reason = 'Scheduling preview cancelled.'): void {
    this.worker?.terminate();
    this.worker = undefined;
    for (const pending of this.pending.values()) {
      pending.cleanup();
      pending.reject(new Error(reason));
    }
    this.pending.clear();
  }

  /** Start one correlated request, rejecting stale replies and cleaning every completion path. */
  private request(
    kind: SchedulingWorkerRequest['kind'],
    input?: string,
    signal?: AbortSignal,
    backend: SchedulingBackend = 'auto',
    weekStart?: string
  ): Promise<SchedulingWorkerResponse> {
    if (signal?.aborted) return Promise.reject(new Error('Scheduling preview cancelled.'));
    if (
      this.pending.size >= 2 ||
      (kind !== 'warm' && [...this.pending.keys()].some(id => !id.startsWith('warm:')))
    ) {
      return Promise.reject(new Error('A scheduling preview is already running.'));
    }
    if (!this.worker) {
      this.worker = this.factory();
      this.worker.onmessage = (event: MessageEvent<SchedulingWorkerReply>) => {
        const pending = this.pending.get(event.data.id);
        if (!pending) return;
        this.pending.delete(event.data.id);
        pending.cleanup();
        if (event.data.error) pending.reject(new Error(event.data.error));
        else pending.resolve({ ...event.data, roundTripMs: performance.now() - pending.started });
      };
      this.worker.onerror = () => this.reset('Browser scheduling Worker failed.');
      this.worker.onmessageerror = () =>
        this.reset('Browser scheduling reply could not be decoded.');
    }
    const worker = this.worker;
    const id = `${kind}:${crypto.randomUUID()}`;
    return new Promise((resolve, reject) => {
      const abort = () => this.reset();
      const timer = setTimeout(() => this.reset('Browser scheduling preview timed out.'), 15_000);
      signal?.addEventListener('abort', abort, { once: true });
      this.pending.set(id, {
        resolve,
        reject,
        started: performance.now(),
        cleanup: () => {
          clearTimeout(timer);
          signal?.removeEventListener('abort', abort);
        },
      });
      try {
        worker.postMessage({
          id,
          kind,
          input,
          backend,
          weekStart,
        } satisfies SchedulingWorkerRequest);
      } catch {
        this.reset('Browser scheduling request could not be sent.');
      }
    });
  }
}
